import { useState } from 'react';
import { X, FileText } from 'lucide-react';
import { api } from '@/services/api';

export default function ContractRenewalModal({ language = 'es', currentLeaseEnd, onClose, onSuccess }) {
  const [notes, setNotes] = useState('');
  const [preferredEnd, setPreferredEnd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.requestContractRenewal({
        notes,
        preferred_lease_end: preferredEnd || null,
      });
      setSent(true);
      onSuccess?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error');
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-50 modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="contract-renewal-modal">
      <div
        className="bg-surface border-2 border-foreground rounded-2xl w-full max-w-md modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-xl text-foreground flex items-center gap-2">
              <FileText className="w-5 h-5" />
              {language === 'es' ? 'Renovación de contrato' : 'Contract renewal'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg text-foreground" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          </div>

          {sent ? (
            <div className="py-8 text-center">
              <div className="w-14 h-14 rounded-full bg-accent-mint border-2 border-foreground mx-auto mb-3 flex items-center justify-center">
                <FileText className="w-7 h-7" />
              </div>
              <h3 className="font-heading font-bold text-lg mb-1">{language === 'es' ? '¡Solicitud enviada!' : 'Request sent!'}</h3>
              <p className="text-sm text-muted-foreground">{language === 'es' ? 'El administrador se va a comunicar con vos para coordinar la renovación.' : 'The admin will contact you to coordinate.'}</p>
              <button
                onClick={onClose}
                className="mt-5 w-full py-3 bg-foreground text-background rounded-xl font-semibold"
                data-testid="renewal-done"
              >
                {language === 'es' ? 'Listo' : 'Done'}
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              {error && <div className="p-3 bg-destructive/20 border border-destructive rounded-lg text-sm text-destructive">{error}</div>}
              {currentLeaseEnd && (
                <div className="p-3 rounded-lg bg-muted/50 border border-border text-sm">
                  <p className="text-xs text-muted-foreground">{language === 'es' ? 'Tu contrato actual vence el' : 'Your current lease ends on'}</p>
                  <p className="font-semibold text-foreground">{currentLeaseEnd}</p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">{language === 'es' ? 'Fecha de renovación deseada' : 'Preferred new end date'}</label>
                <input
                  type="date"
                  value={preferredEnd}
                  onChange={(e) => setPreferredEnd(e.target.value)}
                  className="input-brutal w-full px-4 py-3"
                  data-testid="renewal-preferred-end"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">{language === 'es' ? 'Comentarios (opcional)' : 'Notes (optional)'}</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="input-brutal w-full px-4 py-3 min-h-[100px] resize-none"
                  placeholder={language === 'es' ? 'Contanos si querés renovar por más tiempo, cambiar de habitación, etc.' : 'Tell us if you want a longer term, room change, etc.'}
                  data-testid="renewal-notes"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={onClose} className="flex-1 py-3 border border-border rounded-lg font-medium text-foreground hover:bg-muted">
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-brutal flex-1 py-3 disabled:opacity-50"
                  data-testid="renewal-submit"
                >
                  {submitting ? '...' : (language === 'es' ? 'Enviar solicitud' : 'Send request')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
